import React from "react";
import HubTabs from "../../components/HubTabs";
import RAGTab from "../KnowledgeBase/components/RAGTab";
import DatasetsTab from "../KnowledgeBase/components/DatasetsTab";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};

  return ({
    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: ds.radius?.md ?? 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
      margin: theme.spacing(2),
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "12px 16px 14px",
      flexShrink: 0,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      flexShrink: 0,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    headerActions: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowX: "auto",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    excelTable: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        borderRight: "none",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        verticalAlign: "middle",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
      "& tbody tr:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    zebraRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.danger || "#e53935",
      "&:hover": {
        background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
        color: dsColors.dangerHover || "#c62828",
      },
    },
    statusChip: {
      fontWeight: 600,
      fontSize: 11,
    },
    emptyState: {
      textAlign: "center",
      padding: theme.spacing(6),
      color: theme.palette.text.secondary,
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: theme.spacing(2),
      opacity: 0.3,
    },
    statsBox: {
      display: "flex",
      gap: theme.spacing(3),
      marginBottom: theme.spacing(2),
      flexWrap: "wrap",
      padding: theme.spacing(0, 2),
    },
    statCard: {
      padding: theme.spacing(2, 3),
      borderRadius: 12,
      flex: "1 1 200px",
      minWidth: 200,
      position: "relative",
      overflow: "hidden",
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#fff"),
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      transition: "transform .25s ease, box-shadow .25s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.55)"
          : "0 8px 24px rgba(15,23,42,0.10)",
      },
    },
    statIcon: {
      width: 44,
      height: 44,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    },
    statNumber: {
      fontSize: 28,
      fontWeight: 800,
      lineHeight: 1.1,
      marginTop: 4,
    },
    statLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    sourceChip: {
      fontSize: 11,
    },
    reportSelect: {
      borderRadius: "12px !important",
      "& .MuiSelect-outlined": {
        borderRadius: "12px !important",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "12px !important",
      },
    },
    compactSelect: {
      "& .MuiSelect-select": {
        paddingTop: 9,
        paddingBottom: 9,
        fontSize: 13,
      },
    },
    reportSelectMenu: {
      borderRadius: 14,
      boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
      border: "none",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none",
      marginTop: 8,
      overflowX: "hidden",
      overflowY: "auto",
      ...theme.scrollbarStyles,
      "& .MuiList-root": {
        padding: 6,
      },
    },
    reportSelectMenuItem: {
      borderRadius: 8,
      padding: "9px 12px",
      marginBottom: 4,
      fontSize: 14,
      fontWeight: 500,
      color: isDark ? "#ffffff" : "#1a1a1a",
      "&:hover, &.Mui-focusVisible": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
      "&.Mui-selected, &.Mui-selected:hover": {
        background: "rgba(6, 207, 156, 0.10)",
      },
    },
  })
});

const KnowledgeBaseHub = () => {
  const classes = useStyles();
  const t = (key, fallback) => {
    const fullKey = `knowledgeBase.${key}`;
    const value = i18n.t(fullKey);
    return !value || value === fullKey ? fallback : value;
  };

  const RAGWrapper = () => <RAGTab classes={classes} />;
  const DatasetsWrapper = () => <DatasetsTab classes={classes} purpose="qualification" />;

  const tabs = [
    {
      label: t("tabs.rag", "Base de Conhecimento (RAG)"),
      path: "/knowledge-base",
      Component: RAGWrapper,
    },
    {
      label: t("tabs.qualification", "Qualificação do Agente"),
      path: "/knowledge-base-qualify",
      Component: DatasetsWrapper,
    },
  ];

  return <HubTabs tabs={tabs} />;
};

export default KnowledgeBaseHub;
